import { FilePicker } from "@/components/FilePicker";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getTemplateAssetFile, listTemplateAssets, uploadTemplateAsset, type TemplateAsset } from "@/api/client";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner, ToolbarButton } from "@/components/ui";
import { useObjectURL } from "@/components/useObjectURL";
import { ScrollArea } from "@/components/ScrollArea";

const size = (asset: TemplateAsset) => `${asset.width_px} × ${asset.height_px} px`;

export function TemplateImages() {
  const assets = useQuery({ queryKey: ["template-assets"], queryFn: listTemplateAssets });
  const items = assets.data ?? [];

  return (
    <>
      <UploadImageForm />
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        <h2 className="border-b border-zinc-200 px-4 py-3 text-sm font-semibold">Template images</h2>
        <ErrorBanner error={assets.error} />
        {assets.isLoading ? (
          <EmptyState title="Loading images" detail="Fetching uploaded images." />
        ) : assets.isSuccess && items.length === 0 ? (
          <EmptyState title="No images yet" detail="Upload backgrounds, watermarks and decorations for templates." />
        ) : (
          <ScrollArea label="Template images">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Image</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Size</th>
                  <th className="px-4 py-3 font-medium">Uploaded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((asset) => (
                  <tr key={asset.id}>
                    <td className="px-4 py-3">
                      <Thumbnail asset={asset} />
                    </td>
                    <td className="px-4 py-3 font-medium text-zinc-950">{asset.name}</td>
                    <td className="px-4 py-3 text-zinc-700">{size(asset)}</td>
                    <td className="px-4 py-3 text-zinc-500">{formatDate(asset.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>
    </>
  );
}

function Thumbnail({ asset }: { asset: TemplateAsset }) {
  // There is no thumbnail endpoint, so this is the stored image (at most 5 MB) scaled down.
  const file = useQuery({
    queryKey: ["template-asset-file", asset.id],
    queryFn: () => getTemplateAssetFile(asset.id),
    staleTime: Infinity,
  });
  const url = useObjectURL(file.data);
  return (
    <div className="grid h-16 w-24 place-items-center rounded-md border border-zinc-200 bg-[repeating-conic-gradient(#f4f4f5_0%_25%,#fff_0%_50%)] bg-[length:12px_12px]">
      {url ? (
        <img src={url} alt={asset.name} className="max-h-full max-w-full object-contain" />
      ) : (
        <span className="text-xs text-zinc-500">{file.isError ? "Unavailable" : "Loading..."}</span>
      )}
    </div>
  );
}

function UploadImageForm() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");

  const upload = useMutation({
    mutationFn: (chosen: File) => uploadTemplateAsset(chosen, name.trim() || undefined),
    onSuccess: async () => {
      setFile(null);
      setName("");
      await queryClient.invalidateQueries({ queryKey: ["template-assets"] });
    },
  });

  const field = "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";
  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">Upload an image</h2>
      <FilePicker
        label="Image file"
        accept="image/png,image/jpeg"
        extensions={[".png", ".jpg", ".jpeg"]}
        typeError="Only PNG or JPEG images can be uploaded"
        maxBytes={20 << 20}
        hint="PNG or JPEG, up to 20 MB"
        thumbnail
        value={file}
        onChange={(next) => {
          setFile(next);
          upload.reset();
        }}
      />
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Name (optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Defaults to the file name" className={field} />
        </label>
        <ToolbarButton disabled={!file || upload.isPending} onClick={() => file && upload.mutate(file)}>
          {upload.isPending ? "Uploading..." : "Upload image"}
        </ToolbarButton>
      </div>
      <p className="text-xs text-zinc-500">
        PNG or JPEG, up to 20 MB. Images are scaled to at most 3508 px on the longest edge and must then fit in 5 MB: use
        JPEG for photographic artwork, and PNG where transparency matters, such as watermarks.
      </p>
      {upload.isSuccess ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          Uploaded {upload.data.name} ({size(upload.data)}).
        </p>
      ) : null}
      <ErrorBanner error={upload.error} />
    </div>
  );
}
